import { patLogs } from "../context/utils";

/**
 * One-shot telemetry for the video modules (Stencil / New / Integrated / Video).
 *
 * patLogs fires 2 network requests per call, so every metric here is logged
 * EXACTLY ONCE per session with a short payload. The only exception is the dial,
 * which logs one line per region per colour change (grey -> blue -> green).
 *
 * All entry points are wrapped in try/catch: telemetry must never break the
 * detection loop.
 */

const REGION_SHORT = {
  Front: "F",
  Rear: "R",
  "Side-Left": "SL",
  "Side-Right": "SR",
  "Front-Isometric-Left": "FIL",
  "Front-Isometric-Right": "FIR",
  "Rear-Isometric-Left": "RIL",
  "Rear-Isometric-Right": "RIR",
};

let S = null;

/**
 * Keys already written, kept OUTSIDE the session state so it survives any
 * re-initialization. The callers sit behind a module-level `logbugs` flag that
 * can be flipped back to true (e.g. the back button), so initTelemetry may run
 * again — this registry is what actually guarantees nothing is ever logged
 * twice. One key per metric; the dial uses one key per region.
 */
const sentKeys = new Set();

/** Send at most once per key, ever. */
const sendOnce = (key, msg) => {
  try {
    if (!S || sentKeys.has(key)) return;
    sentKeys.add(key);
    patLogs(msg, S.inspectionId);
  } catch (e) {
    /* telemetry only */
  }
};

/**
 * Start a telemetry session. Idempotent: repeated calls (re-render, re-entry
 * via the back button) keep the existing session and never re-arm the logs.
 */
export const initTelemetry = (inspectionId) => {
  if (S && S.inspectionId === inspectionId) return;
  S = {
    inspectionId,
    t0: performance.now(),
    modelStart: 0,
    infTimes: [],
    dialActive: null, // region currently being worked on
    dialRec: {}, // region -> { enter, blue, green }
  };
};

/** Model initialization started. */
export const logModelInitStart = () => {
  try {
    if (!S || sentKeys.has("mdlStart")) return;
    S.modelStart = performance.now();
    sendOnce("mdlStart", "mdl init start");
  } catch (e) {
    /* telemetry only */
  }
};

/**
 * Model initialization finished. `loadMs` (optional) is the loader's own
 * measurement; otherwise the elapsed time since logModelInitStart is used.
 */
export const logModelInitEnd = (loadMs) => {
  try {
    if (!S || sentKeys.has("mdlEnd")) return;
    const ms =
      loadMs !== undefined && loadMs !== null && !isNaN(loadMs)
        ? Number(loadMs)
        : performance.now() - S.modelStart;
    sendOnce("mdlEnd", `mdl init end ${ms.toFixed(0)}ms`);
  } catch (e) {
    /* telemetry only */
  }
};

/**
 * One inference (frame -> detections). Logs the first frame that actually
 * detects something, and the average over the first 5 frames. Both once.
 */
export const recordInference = (ms, detectionCount) => {
  try {
    if (!S) return;

    if (detectionCount > 0) {
      sendOnce("infFirst", `inf first ${ms.toFixed(0)}ms det=${detectionCount}`);
    }

    // Only the first 5 samples are ever collected, then the average is sent once.
    if (S.infTimes.length < 5 && !sentKeys.has("infAvg5")) {
      S.infTimes.push(ms);
      if (S.infTimes.length === 5) {
        const avg = S.infTimes.reduce((a, b) => a + b, 0) / 5;
        sendOnce("infAvg5", `inf avg5 ${avg.toFixed(0)}ms`);
      }
    }
  } catch (e) {
    /* telemetry only */
  }
};

/**
 * RF angle -> gyro handoff, logged once when both are available: RF compute
 * time, RF angle and the gyro angle it is assigned to. The raw device
 * orientation is logged on the instructions page instead, right after the gyro
 * permission is granted.
 */
export const recordAngle = (guidance, rfAngle, gyroAngle) => {
  try {
    if (!S || sentKeys.has("angle")) return;
    if (rfAngle === undefined || rfAngle === null || rfAngle === -1) return;

    const rfMs = guidance && guidance.lastAngleLogicMs;

    sendOnce(
      "angle",
      `rf ${rfMs ? rfMs.toFixed(1) : "-"}ms a=${rfAngle} gyro=${
        gyroAngle === undefined || gyroAngle === null ? "-" : gyroAngle
      }`,
    );
  } catch (e) {
    /* telemetry only */
  }
};

/**
 * Emit one summary line for a region: time from entering it to blue / green.
 * Keyed per region, so a region is never logged twice even if it is revisited.
 */
const flushRegion = (region) => {
  const rec = S && S.dialRec[region];
  if (!rec || sentKeys.has(`dial:${region}`)) return;
  const d = (t) => (t === null ? "-" : ((t - rec.enter) / 1000).toFixed(1) + "s");
  sendOnce(
    `dial:${region}`,
    `dial ${REGION_SHORT[region] || region} b=${d(rec.blue)} g=${d(rec.green)}`,
  );
};

/**
 * Dial timing. Per region we track when it was entered (grey) and when it first
 * turned blue (partial) / green (full). Nothing is sent while the region is
 * active: the summary for the PREVIOUS region is flushed as soon as the region
 * changes, so each region produces exactly one short line.
 */
export const recordDial = (regionsCoveredMap, currentRegion) => {
  try {
    if (!S || !regionsCoveredMap) return;
    const now = performance.now();

    // Track the first time each region turns blue / green.
    Object.keys(regionsCoveredMap).forEach((region) => {
      const state = regionsCoveredMap[region];
      if (!state) return; // 0 = grey, nothing to time yet
      const rec =
        S.dialRec[region] ||
        (S.dialRec[region] = { enter: now, blue: null, green: null });
      if (state >= 1 && rec.blue === null) rec.blue = now;
      if (state >= 2 && rec.green === null) rec.green = now;
    });

    // Region changed -> log the previous region's accumulated data.
    if (currentRegion && currentRegion !== S.dialActive) {
      if (S.dialActive) flushRegion(S.dialActive);
      if (!S.dialRec[currentRegion])
        S.dialRec[currentRegion] = { enter: now, blue: null, green: null };
      S.dialActive = currentRegion;
    }
  } catch (e) {
    /* telemetry only */
  }
};

/** Flush the region still in progress (call when recording stops). */
export const flushDial = () => {
  try {
    if (S && S.dialActive) flushRegion(S.dialActive);
  } catch (e) {
    /* telemetry only */
  }
};
